import { EMPTY_URL } from "csi/constants";
import { ICustomViewGalleryOptions, ICustomViewOptions, IViewSettingOptions } from "csi/middleware/customView/options/CustomViewOptions";
import { DojoViewProps } from "csi/page/DojoView";
import csiPrefs from "csi/prefs";
import { Defaults } from "csi/prefs/Defaults";
import { PreferenceView } from "csi/prefs/PreferenceView";
import { resolveNodes } from "csi/service/nodeService";
import { TABLE_ID } from "csi/service/viewService";
import { submitWriter } from "csi/service/writerService";
import WorkingCache from "csi/state/WorkingCache";
import { C8Node, C8NodeType, DUMMY, ICache, cnl } from "csi/typings";
import { ReactionStore } from "csi/utility/mobxUtility";
import xml from "csi/xml";
import ChangeNode from "csi/xml/ChangeNode";
import Writer from "csi/xml/Writer";
import { ILocaleProvider } from "designSystem/locale";
import { preventDefaultBehaviour } from "designSystem/utilities/prevent-default-behavior";
import { clone, isEqual } from "lodash";
import { action, computed, makeObservable, observable, reaction, runInAction } from "mobx";
import { ChangeEvent, FunctionComponent, SyntheticEvent, createContext, useContext } from "react";
import { ICustomViewInfoResponse, getValidatedCustomView, queryCustomViews } from "src/c8-platform/service/customViewBuilderService";
import { getDialogZIndex } from "csi/utility/htmlUtility";
import AttributesTab from "./attributes/AttributesTab";
import CustomViewAttributesStore from "./attributes/CustomViewAttributesStore";
import CompositeTab from "./composite/CompositeTab";
import CustomViewCompositeStore from "./composite/CustomViewCompositeStore";
import { CustomViewItem } from "./customViewLeftPanel/CustomViewItem";
import { CustomViewNodeContexts } from "./customViewLeftPanel/CustomViewNodeContexts";
import { CustomViewStore } from "./customViewLeftPanel/CustomViewStore";
import { NonGlobalCustomView, generateDefaultCustomViewInfo, getCustomViewResult, getDefaults, validateUniqueCustomViewName } from "./customViewLeftPanel/customViewDialogLeftPanelService";
import { writeCustomView } from "./customViewSaveService";
import CustomViewDataManagementStore from "./data/CustomViewDataManagementStore";
import DataManagementTab from "./data/DataManagementTab";
import { ICustomViewImageFormatOptions } from "./options/CustomViewOptions";
import CustomViewOptionsStore from "./options/CustomViewOptionsStore";
import CustomViewSettingsStore from "./settings/CustomViewSettingsStore";
import SettingsTab from "./settings/SettingsTab";

// dnd attribute types for drag and drop
export enum DND_TYPE {
	CUSTOM_VIEW_ATTRIBUTE = "CUSTOM_VIEW_ATTRIBUTE",
	CUSTOM_VIEW_SELECTED_ATTRIBUTE = "CUSTOM_VIEW_SELECTED_ATTRIBUTE",
	CUSTOM_VIEW_SELECTED_GROUP = "CUSTOM_VIEW_SELECTED_GROUP",
}

export type OnSaveCustomViewFunction = (cn: ChangeNode, options: ICustomViewOptions) => void;
export type NewCustomViewFromNodeFunction = (node: C8Node, sourceNode?: C8Node) => PreferenceView;

const VIEW_SETTINGS_TAB_ID = "CustomViewViewSettingsTab";

/**
 * Custom view dialog root mobx store that facilitates communication between various custom view stores.
 */

class CustomViewTab {
	/** component to render tab */
	Component: FunctionComponent;

	/** id and localization resource for tab */
	id: string;

	/** indicates if tab has already been visited */
	@observable visited = false;

	constructor(id: string, component: FunctionComponent) {
		makeObservable(this);
		this.Component = component;
		this.id = id;
	}
}

export default class CustomViewDialogStore extends ReactionStore {
	STANDARD_PADDING = "12px 24px";

	// cache previous values to reduce preview renders 
	#cachedPreviewParams: Record<string, any>;
	#cachedPreviewProps: DojoViewProps;

	/** indicates preview can be suspended (and should start out suspended by default) */
	allowSuspendPreview = false;

	/** store supporting attribute selection */
	@observable.ref attributesStore?: CustomViewAttributesStore;

	/** list of available tabs */
	@observable.ref availableTabs: CustomViewTab[] = [];

	/** indicate if aware of validation errors */
	@observable awareOfValidationErrors: boolean = true;

	/** anchor map to pop up the default custom view info */
	#anchorElMap = observable.map<cnl, HTMLButtonElement>(null, { deep: false });

	/** if set to true then hide left pane and View Settings tab */
	readonly manageSingleCustomView?: boolean;

	/** cache holding the selected custom view */
	cache: ICache;

	/** keep track of defaults views that have been changed */
	changedContexts: string[] = [];

	/** store supporting editing contents of composite view */
	compositeViewStore?: CustomViewCompositeStore;

	/** the custom view item to duplicate when there's one waiting to be saved */
	@observable.ref confirmDuplicationItem: CustomViewItem;

	/** the custom view item to confirm when it becomes dirty */
	@observable.ref confirmSelectionItem: CustomViewItem;

	/** container of the custom view */
	readonly customViewContext: string;

	/** contexts for the custom view node, for example, ProductType for the Style  */
	readonly customViewNodeContexts: CustomViewNodeContexts;

	/** custom view root node type  */
	readonly customViewNodeType: C8NodeType;

	/** custom view container URL */
	readonly customViewContainerUrl: cnl;

	/** path hints to store with custom view container */
	readonly customViewPathHints?: string;

	/** custom view result to serve the components */
	@observable.ref customViewStore: CustomViewStore;

	/** store supporting editing contents of composite view */
	dataManagementStore?: CustomViewDataManagementStore;

	/** Open question - do we need to store this info at all?  */
	/** list map to hold the default custom view info for being clicked party(either role based level or global level), key is the clicked party  */
	defaultCustomViewOfSelectedGroupMap = observable.map<cnl, CustomViewItem[]>(null, { deep: false });

	/** Defaults instance to hold the defaults information */
	#defaults: Defaults = null;

	/** deleting error during deleting the custom view node  */
	@observable failDeletingError: string;

	/** The relative type for filters in the current view. */
	readonly filterRelativeType: C8NodeType;

	/** indicate if we're forcing duplicating custom view, for example, SaveAs action in the toolbar */
	forceDuplicate: boolean = false;

	/** the initial option values to begin with */
	@observable.ref initialOptionValues: ICustomViewOptions;

	/** indicate if saving the custom view */
	@observable isSaving: boolean = false;

	/** indicate if the left panel is open or not */
	@observable leftPanelOpen: boolean = true;

	/** locale provider */
	loc?: ILocaleProvider;

	/** store: ArrayDataStore<NamesStoreItem> - supporting names tab */
	imageFormatStore?: any;

	/** store: ArrayDataStore<NamesStoreItem> - supporting names tab */
	namesStore?: any;

	/** function to create a custom view from a given _CS_PreferenceView node */
	readonly newInstanceFromNode: NewCustomViewFromNodeFunction;

	/** function to add to ChangeNode after saving custom view */
	readonly onSaveCustomView: OnSaveCustomViewFunction;

	/** the OOTB custom view  */
	readonly OOTBCustomView: PreferenceView;

	/** store supporting editing contents of options view */
	optionsStore?: CustomViewOptionsStore<ICustomViewOptions>;

	/** cache holding party nodes */
	readonly partyCache = new WorkingCache();

	/** initial properties for preview */
	readonly #previewProps: DojoViewProps;

	/** store supporting editing contents of settings view */
	settingStore?: CustomViewSettingsStore;

	/** type of target class for view */
	readonly targetType: C8NodeType;

	/** cnl of selected custom view */
	@observable selectedCustomViewCnl: cnl;

	/** selected tab index */
	@observable selectedTab = "";

	/** indicates if preview will show live data */
	@observable showDataInPreview = true;

	/** indicate if the current selected view exists or not */
	@observable viewDoesNotExist: boolean;

	/** the defaults information based on the current context */
	get defaults(): Defaults {
		this.#defaults ||= getDefaults();

		return this.#defaults;
	}

	/** indicate if we have personal access to the custom view */
	get hasPersonalAccess(): boolean {
		return csiPrefs.hasPersonalAccess();
	}

	/** indicate whether the current view is composite view */
	get isCompositeView(): boolean {
		return !!this.compositeViewStore;
	}

	/** option values for export names */
	@computed get namesOptionValues(): ICustomViewOptions | undefined {
		const { namesStore } = this;

		if (namesStore) {
			const { get, total } = namesStore;
			const namesSettings: Record<string, string> = {};

			for (let i = 0; i < total; ++i) {
				const item = get(i);
				namesSettings[item.key] = item.name;
			}

			return { namesSettings };
		}
	}

	/** indicate whether or not open the confirmation of selection dialog */
	get openConfirmationSelectionDialog(): boolean {
		return !!this.confirmSelectionItem;
	}

	/** indicate whether or not open the confirmation of duplication dialog */
	get openConfirmationDuplicationDialog(): boolean {
		return !!this.confirmDuplicationItem;
	}

	/** indicate whether or not open the warning dialog due to view */
	get openViewDoesNotExistWarningDialog(): boolean {
		return this.viewDoesNotExist;
	}

	/** indicate whether or not open the error dialog due to the validation error */
	get openViewValidationErrorDialog(): boolean {
		return !this.awareOfValidationErrors && !!this.settingStore.nameErrorRes;
	}

	/** indicate if the selected view is OOTB view */
	get selectedViewIsOOTB(): boolean {
		return this.selectedCustomViewCnl === EMPTY_URL;
	}

	/** indicate if selected view is the system wide default view */
	get selectedViewIsSystemWideDefault(): boolean {
		const { customViewContext, defaults, selectedCustomViewCnl } = this;
		return defaults.get(customViewContext, EMPTY_URL, true) === selectedCustomViewCnl;
	}

	/** indicate whether the header action menu button should be shown */
	get showActionMenuButton(): boolean {
		const { customViewStore:
			{
				selectedCustomViewItem,
				showDiscard,
				showSaveAsInMore,
			},
		} = this;

		const {
			hideDeleteButton,
			showAddToFavorite,
			showRemoveFromFavorite,
		} = selectedCustomViewItem || {}; // the intermediate state could be null, must add {}

		return showAddToFavorite || showRemoveFromFavorite || showDiscard || showSaveAsInMore || !hideDeleteButton;
	}

	/** current view options based on store state */
	@computed get optionValues(): ICustomViewOptions & IViewSettingOptions & ICustomViewImageFormatOptions {
		const {
			attributesStore,
			compositeViewStore,
			dataManagementStore,
			imageFormatStore,
			namesOptionValues,
			optionsStore,
			settingStore,
		} = this;

		const optionValues = optionsStore.options;
		const attributeValues = attributesStore?.optionValues;
		const compositeValues = compositeViewStore?.optionValues;
		const dataValues = dataManagementStore?.optionValues;
		const imageExportFormatOptionValues = imageFormatStore?.optionValues;
		const settingValues = settingStore?.optionValues;

		return {
			...optionValues,
			...attributeValues,
			...compositeValues,
			...dataValues,
			...namesOptionValues,
			...imageExportFormatOptionValues,
			...settingValues,
		};
	}

	/** dynamic parameters for preview incorporating changes from custom view dialog */
	@computed get previewParams(): Record<string, any> {
		const { allowSuspendPreview, OOTBCustomView, optionValues, showDataInPreview } = this;
		const suspendValues = allowSuspendPreview ? { suspended: !showDataInPreview, suspendIsOption: true } : null;

		// omit these properties which is causing preview panel update on every single key press
		const {
			prefDescription,
			prefIsShared,
			prefName,
			prefParty,
			...previewValues
		} = optionValues;

		OOTBCustomView.customOptions.forEach(({ name, omitFromPreview }) => {
			if (omitFromPreview) {
				delete previewValues[name];
			}
		});

		return {
			...previewValues,
			...suspendValues,
		};
	}

	/** properties for preview */
	@computed get previewProps(): DojoViewProps | undefined {
		const { viewParams, ...viewProps } = this.#previewProps;

		// for now, preview is not available for composite views, or tables in Go to Dialog;
		// restrict preview for UpdateExistingPartMaterials custom view due to way in which view is constructed
		if (this.isCompositeView ||
			viewProps.viewId === TABLE_ID ||
			viewProps.viewId === "AddNewPartMaterials" ||
			viewProps.viewId === "DeletePartMaterials" ||
			viewProps.viewId === "UpdateExistingPartMaterials"
		) {
			return;
		}

		const { previewParams } = this;

		if (isEqual(this.#cachedPreviewParams, previewParams)) {
			return this.#cachedPreviewProps;
		}

		this.#cachedPreviewParams = previewParams;
		this.#cachedPreviewProps = {
			...viewProps,
			forceRefresh: new Date().getTime(),
			viewParams: {
				...previewParams,
				...viewParams,
			},
		};

		return this.#cachedPreviewProps;
	}

	/** role views set */
	get roleBasedCustomViewSet(): Set<cnl> {
		return this.customViewStore.roleItemSet;
	}

	/** the selected custom view name */
	get selectedCustomViewName(): string | undefined {
		const { selectedCustomViewCnl, customViewStore: { names } } = this;
		return names[selectedCustomViewCnl] || this.customViewStore.duplicatedPreferenceView?.name;
	}

	/** indicate if the current selected view is dirty */
	get viewIsDirty(): boolean {
		return !this.selectedCustomViewCnl || !isEqual(this.initialOptionValues, this.optionValues);
	}

	/** z-index for the dialog */
	@computed({ keepAlive: true })
	get zIndex(): number {
		return getDialogZIndex();
	}

	/** z-index for the popup above the main dialog */
	get zIndexForPopup(): number {
		return this.zIndex + 1;
	}

	/** confirm view doesn't exist */
	@action confirmViewDoesNotExist = (): void => {
		this.viewDoesNotExist = false;
	};

	/** confirm we're aware of the error */
	@action confirmValidationError = (): void => {
		this.awareOfValidationErrors = true;
	};

	/** continue selecting view */
	private continueSelectCustomView = async (item: CustomViewItem, saveCurrent: boolean = false, discard: boolean = false): Promise<void> => {
		const {
			customViewStore,
			getValidatedPreferenceView,
			prepareToOpen,
			saveCustomView,
			selectedCustomViewCnl,
			updateStores,
		} = this;
		this.confirmSelectionItem = null;

		if (saveCurrent) {
			const cvNode = await saveCustomView(selectedCustomViewCnl, false);

			if (!cvNode) {
				runInAction(() => {
					this.confirmSelectionItem = null;
				});

				return;
			}
		} else if (customViewStore.duplicatedCustomViewItem !== item && discard) {
			// discard the duplicated custom view
			customViewStore.cancelDuplicateCustomView();
		}

		// we must use this.customViewStore (rather than the destructuring one) to get the latest version, because we will create another customViewStore in the function saveCustomView during name validation
		let cv = this.customViewStore.duplicatedPreferenceView || await getValidatedPreferenceView(item.url);

		runInAction(async () => {
			if (!cv) {
				// view doesn't exist anymore, go to the default view and refresh the custom view store
				this.viewDoesNotExist = true;
				await this.refreshCustomViewStore(false);
				cv = await getValidatedPreferenceView(customViewStore.defaultCustomView.url);
			}

			runInAction(async () => {
				await updateStores(cv);
				prepareToOpen();
			});
		});
	};

	/** create tabs based on available stores */
	@action createTabs = (): void => {
		const {
			attributesStore,
			compositeViewStore,
			dataManagementStore,
			manageSingleCustomView,
			namesStore,
			optionsStore,
		} = this;

		const tabs: CustomViewTab[] = this.availableTabs = [];
		const attributesOnly = attributesStore?.options.filterOnly;

		if (attributesStore && !attributesStore.options.fixed) {
			tabs.push(new CustomViewTab("CustomViewAttributesTab", AttributesTab));

			// add reaction for searching custom view attributes
			this.trackReaction(reaction(
				() => [attributesStore.searchText, attributesStore.selectedLevel],
				() => {
					attributesStore.selectedLevel.searchLevel(attributesStore.searchText);
				},
			));
		}

		if (namesStore) {
			tabs.push(new CustomViewTab("CustomViewNamesTab", namesStore.component));
			/** react on adding/removing attribute in attributes selection view */
			this.trackReaction(reaction(
				() => attributesStore.attributeSpecsInView,
				() => namesStore.refreshData(),
			));
		}

		if (compositeViewStore) {
			tabs.push(new CustomViewTab("CustomViewViewSelectionTab", CompositeTab));
		}

		if (dataManagementStore) {
			const { groupingStore, dataManagementOptions: { isGalleryView } } = dataManagementStore;
			tabs.push(new CustomViewTab("CustomViewDataManagementTab", DataManagementTab));

			// update querySize whenever we change column/row attribute groups in gallery view
			if (isGalleryView) {
				this.trackReaction(reaction(() => groupingStore?.currentGroupSettings, () => this.updateQuerySizeInOptionStore()));
			}

			this.trackChildStore(dataManagementStore.reportStore);
		}

		if (optionsStore?.component && !attributesOnly) {
			tabs.push(new CustomViewTab("CustomViewDisplayOptionsTab", optionsStore.component));
		}

		if (!manageSingleCustomView) {
			tabs.push(new CustomViewTab(VIEW_SETTINGS_TAB_ID, SettingsTab));
		}

		this.trackChildStore(attributesStore);
		this.trackChildStore(optionsStore);

		// ensure first tab is rendered
		const firstTab = tabs[0];
		firstTab.visited = true;
		this.selectedTab = firstTab.id;
	};

	/** get the anchor value by using the key */
	getAnchorEl = (partyUrl: cnl): HTMLButtonElement => {
		return this.#anchorElMap.get(partyUrl);
	};

	/** get default custom view info */
	@action getDefaultCustomViewInfo = (customViewGroup: NonGlobalCustomView | null): void => {
		const roleCnl = customViewGroup?.url;
		const partyUrl = roleCnl || DUMMY;

		if (!!this.defaultCustomViewOfSelectedGroupMap.get(partyUrl)) {
			return;
		}

		const {
			customViewNodeContexts,
			customViewStore: { names },
			defaults,
			customViewContext,
			loc,
			roleBasedCustomViewSet,
		} = this;

		const defaultCustomViews: CustomViewItem[] = generateDefaultCustomViewInfo(
			roleCnl,
			customViewContext,
			customViewNodeContexts,
			names,
			loc,
			defaults,
			roleBasedCustomViewSet,
		);

		this.defaultCustomViewOfSelectedGroupMap.set(partyUrl, defaultCustomViews);
	};

	/** get validated version of PreferenceView, get rid of the invalid attributes(disabled, deleted...) */
	getValidatedPreferenceView = async (customViewCnl: cnl): Promise<PreferenceView> => {
		let cvNode = null;

		if (customViewCnl !== EMPTY_URL) {
			cvNode = await getValidatedCustomView(customViewCnl);
			this.cache.cacheNode(cvNode);
		}

		return this.newInstanceFromNode(cvNode);
	};

	/** close the popup */
	@action handleCloseDefaultCustomViewPopup = (event: React.MouseEvent<HTMLElement>, partyUrl: cnl): void => {
		this.#anchorElMap.set(partyUrl, null);
		preventDefaultBehaviour(event);
	};

	/** set the selected tab and mark it visited so it will render */
	@action onChangeTab = (e: SyntheticEvent<Element, Event>, value: string): void => {
		const tab = this.availableTabs.find(({ id }) => id === value);
		tab.visited = true;
		this.selectedTab = value;
		this.settingStore.resetShareViewWithOptions();
	};

	/** cancel button callback */
	@action onCancelNavigatingToNewCV = (): void => {
		const { confirmSelectionItem, continueSelectCustomView } = this;
		continueSelectCustomView(confirmSelectionItem, false, true);
	};

	/** confirm button callback to cancel duplicate */
	@action onConfirmCancelDuplicate = (): void => {
		const { confirmDuplicationItem, customViewStore } = this;
		this.confirmDuplicationItem = null;
		customViewStore.cancelDuplicateCustomView();
		confirmDuplicationItem.continueDuplicating(true);
	};

	/** confirm button callback to save duplicate */
	@action onConfirmSaveDuplicate = async (): Promise<void> => {
		const { confirmDuplicationItem, customViewStore } = this;
		const { duplicatedCustomViewItem } = customViewStore;
		const ret = await duplicatedCustomViewItem.save();

		if (!ret) {
			// save failing, return
			runInAction(() => {
				this.confirmDuplicationItem = null;
			});

			return;
		}

		confirmDuplicationItem.continueDuplicating(true);

		runInAction(() => {
			this.confirmDuplicationItem = null;
		});
	};

	/** confirm button callback */
	@action onConfirmNavigatingToNewCV = (): void => {
		const { confirmSelectionItem, continueSelectCustomView } = this;
		continueSelectCustomView(confirmSelectionItem, true);
	};

	/** fetch the party info and copy the initial states to prepare for the custom view opening/rendering  */
	@action prepareToOpen = async (firstLoading: boolean = false): Promise<void> => {
		const { customViewStore, optionValues, resolveParty } = this;
		// update the original states
		this.initialOptionValues = clone(optionValues);

		if (this.forceDuplicate) {
			// duplicate the current selected view
			customViewStore.selectedCustomViewItem.saveAs();
			this.forceDuplicate = false;
		} else if (firstLoading) {
			const { getValidatedPreferenceView, OOTBCustomView, selectedCustomViewCnl } = this;
			let initialPreferenceView = OOTBCustomView;

			if (xml.isValidURL(selectedCustomViewCnl)) {
				initialPreferenceView = await getValidatedPreferenceView(selectedCustomViewCnl);
			}

			runInAction(() => {
				customViewStore.selectedPreferenceView = initialPreferenceView;
			});
		}

		// ensure that all members of party are cached
		await resolveParty();
	};

	/** refresh custom view store */
	@action refreshCustomViewStore = async (isSaving: boolean): Promise<ICustomViewInfoResponse> => {
		const { customViewContainerUrl, customViewNodeType, customViewStore, selectedCustomViewCnl } = this;
		const availableCustomViews = await queryCustomViews(customViewContainerUrl, customViewNodeType);
		const newStore = getCustomViewResult(availableCustomViews, selectedCustomViewCnl, this);

		runInAction(() => {
			// preserve the duplicate one if any
			customViewStore.onRefreshStore(newStore, isSaving);

			// reinitialize the custom view store to get the latest data
			this.customViewStore = newStore;
		});

		if (newStore.containsOOTBDefaultView) {
			availableCustomViews.systemWide.push(EMPTY_URL);
		}

		return availableCustomViews;
	};

	/** reset failDeletingError */
	@action resetDeletingErrorMessage = (): void => {
		this.failDeletingError = "";
	};

	/** ensure that nodes in custom view party are cached */
	resolveParty = async (): Promise<void> => {
		const { prefParty, updateSpecificRoles } = this.settingStore;
		await resolveNodes(prefParty, this.partyCache);
		updateSpecificRoles();
	};

	/** reset custom view to recover from the error */
	@action resetError = (): void => {
		const { selectCustomView, selectedCustomViewCnl, setError } = this;
		selectCustomView(new CustomViewItem(selectedCustomViewCnl, null, null));
		setError(null);
	};

	/** 
	 * determine whether or not to refresh the custom view store
	 * if the permission changes, from personal to system-wide, role-based to personal etc., we need to refresh the custom view store to reorganize the views so that they can go to the appropriate group with the right order
	 */
	requireRefreshCustomViewStore = (cvNode: C8Node): [boolean, PreferenceView] => {
		const { customViewStore, newInstanceFromNode } = this;
		const originalParty = customViewStore.selectedPreferenceView.getParty();
		const latestPreferenceView = newInstanceFromNode(cvNode);
		const bRefresh = !latestPreferenceView.isSameParty(originalParty);

		return [bRefresh, latestPreferenceView];
	};

	/** save custom view */
	@action saveCustomView = async (customViewItemUrl: cnl, isSaveAndClose: boolean): Promise<C8Node | undefined> => {
		const isDuplicated = !xml.isValidURL(customViewItemUrl);
		let cvNode: C8Node;
		this.isSaving = true;

		try {
			// validate first
			const valid = await this.validate();

			if (!valid) {
				return;
			}

			const writer = new Writer();
			const {
				cache,
				customViewContainerUrl,
				customViewPathHints,
				defaultCustomViewOfSelectedGroupMap,
				manageSingleCustomView,
				onSaveCustomView,
				optionValues,
				refreshCustomViewStore,
				requireRefreshCustomViewStore,
				selectedCustomViewCnl,
				settingStore: {
					processDefaults,
				},
				targetType,
			} = this;

			const cn = writeCustomView(writer, customViewContainerUrl, customViewPathHints, optionValues, selectedCustomViewCnl, targetType, null);
			onSaveCustomView(cn, optionValues);
			const response = await submitWriter(writer);
			cvNode = response.getNodeOfType(csiPrefs.TYPE_VIEW);

			if (!cvNode) {
				return;
			}

			// set defaults for site/role/user preferences, skip if managing single custom view
			if (!manageSingleCustomView) {
				await processDefaults(writer, cvNode);
			}

			const { customViewStore } = this;
			const url = cvNode.$URL;
			let bRefreshCustomViewStore = false;

			runInAction(() => {
				if (!isSaveAndClose) {
					const [refresh, latestPreferenceView] = requireRefreshCustomViewStore(cvNode);
					bRefreshCustomViewStore = refresh;

					if (bRefreshCustomViewStore) {
						// update selectedPreferenceView so that the permission would recalculate based on the new permissions
						this.customViewStore.selectedPreferenceView = latestPreferenceView;
					}

					defaultCustomViewOfSelectedGroupMap.clear();
				}

				if (isDuplicated) {
					const { clearDuplicated, duplicatedCustomViewItem } = customViewStore;

					// substitute the url and name with the newly saved one in the case of duplication
					if (duplicatedCustomViewItem) {
						duplicatedCustomViewItem.url = url;
						duplicatedCustomViewItem.name = cvNode.$Name;
						this.selectedCustomViewCnl = url;
					}

					clearDuplicated();
				}

				// the name may change
				customViewStore.selectedCustomViewItem.name = cvNode.$Name;

				// update the original states
				this.initialOptionValues = clone(this.optionValues);

				// update the cache with the response
				cache.cacheResponse(response);
			});

			if (bRefreshCustomViewStore) {
				await refreshCustomViewStore(true);
			}
		} catch (err) {
			this.setError(err);
		} finally {
			runInAction(() => {
				this.isSaving = false;
			});
		}

		return cvNode;
	};

	/** callback for view selection */
	@action selectCustomView = (item: CustomViewItem, ignoreDirty: boolean = false): void => {
		const { continueSelectCustomView, viewIsDirty } = this;

		if (!ignoreDirty && viewIsDirty) {
			this.confirmSelectionItem = item;
		} else {
			continueSelectCustomView(item);
		}
	};

	/** select default custom view */
	@action selectDefaultCustomView = (event: React.MouseEvent<HTMLButtonElement>, item: CustomViewItem, partUrl: cnl): void => {
		this.selectCustomView(item);
		this.setAnchorEl(partUrl, null);
		preventDefaultBehaviour(event);
	};

	/** set the anchor map */
	@action setAnchorEl = (partyUrl: cnl, anchorEl: HTMLButtonElement): void => {
		this.#anchorElMap.set(partyUrl, anchorEl);
	};

	@action setLeftPanelOpen = (): void => {
		this.leftPanelOpen = !this.leftPanelOpen;
	};

	/** turn on or off suspend mode in preview */
	@action setShowDataInPreview = (event: ChangeEvent<HTMLInputElement>, checked: boolean): void => {
		this.showDataInPreview = checked;
	};

	/** switch to view settings tab to show the errors */
	switchTabToViolation = (): void => {
		const { onChangeTab } = this;
		onChangeTab(null, VIEW_SETTINGS_TAB_ID);
	};

	/* force update the defaults info */
	updateDefaults = (): void => {
		this.#defaults = getDefaults();
	};

	/** update the stores to reflect the new selected custom view */
	@action private updateStores = async (cv: PreferenceView): Promise<void> => {
		const { values, urlNode } = cv;
		this.selectedCustomViewCnl = urlNode;

		const {
			attributesStore,
			compositeViewStore,
			customViewStore,
			dataManagementStore,
			imageFormatStore,
			namesStore,
			optionsStore,
			settingStore,
		} = this;

		const { prefDescription, prefIsShared, prefName, prefParty, ...optionValues } = values;

		// update the states for the UI to reaction
		customViewStore.selectedPreferenceView = cv;

		// update the stores
		attributesStore?.onChangeOptions(optionValues);

		// update options store
		optionsStore?.onChangeOptions(optionValues);

		// update data management store
		dataManagementStore?.onChangeOptions(optionValues);
		if (dataManagementStore?.dataManagementOptions.isGalleryView) {
			this.trackReaction(reaction(() => dataManagementStore.groupingStore?.currentGroupSettings, () => this.updateQuerySizeInOptionStore()));
		}

		// update image format export store
		imageFormatStore?.onChangeOptions(optionValues);

		// update names store
		namesStore?.onChangeOptions(optionValues);

		// update settings store
		settingStore.onChangeOptions({ prefDescription, prefIsShared, prefName, prefParty });

		// update compositeViewStore 
		await compositeViewStore?.onChangeOptions(optionValues.nestedViews);
	};

	/** validation */
	validate = async (): Promise<boolean> => {
		const {
			settingStore: {
				activeName,
			},
			switchTabToViolation,
			validateCompositeViewPermissions,
			validateViewNameUnique,
		} = this;

		// check if name is empty
		if (!activeName) {
			switchTabToViolation();
			return false;
		}

		// check if name is unique
		const isValid = await validateViewNameUnique();

		if (!isValid) {
			switchTabToViolation();
			runInAction(() => {
				this.awareOfValidationErrors = false;
			});

			return false;
		}

		// validate permission
		const permissionIsValid = await validateCompositeViewPermissions();

		return permissionIsValid;
	};

	/** validate view name */
	validateViewNameUnique = async (): Promise<boolean> => {
		const {
			manageSingleCustomView,
			refreshCustomViewStore,
			selectedCustomViewCnl,
			settingStore: {
				activeName,
				shareWith,
			},
		} = this;

		const availableCustomViews = await refreshCustomViewStore(false);

		return manageSingleCustomView || validateUniqueCustomViewName(availableCustomViews, selectedCustomViewCnl, activeName, shareWith);
	};

	/** validate composite view permission */
	validateCompositeViewPermissions = async (): Promise<boolean> => {
		return await this.compositeViewStore?.validate() ?? true;
	};

	/** update and disable changing query size in options tab if either group attributes are selected */
	@action private updateQuerySizeInOptionStore = (): void => {
		const {
			dataManagementStore: {
				dataManagementOptions: { isGalleryView },
				groupingStore: { currentGroupSettings: { key1: columnGroupAttribute, key2: rowGroupAttribute } },
			},
			optionsStore,
		} = this;

		// if this is a gallery view with either group attributes, set querySize to 0. Otherwise, set it to the default (20)
		(optionsStore.options as ICustomViewGalleryOptions).querySize = isGalleryView && (columnGroupAttribute || rowGroupAttribute) ? 0 : 20;
	};

	constructor(
		loc: ILocaleProvider,
		previewProps: DojoViewProps,
		manageSingleCustomView: boolean,
		cache: ICache,
		customViewNodeType: C8NodeType,
		selectedCustomViewCnl: cnl,
		availableCustomViews: ICustomViewInfoResponse,
		customViewContainerUrl: cnl,
		customViewContext: string,
		customViewNodeContext: CustomViewNodeContexts,
		customViewPathHints: string,
		targetType: C8NodeType,
		onSaveCustomView: OnSaveCustomViewFunction,
		OOTBCustomView: PreferenceView,
		newInstanceFromNode: NewCustomViewFromNodeFunction,
		forceDuplicate: boolean,
		filterRelativeType?: C8NodeType,
	) {
		super();
		makeObservable(this);
		this.loc = loc;
		this.#previewProps = previewProps;
		this.selectedCustomViewCnl = selectedCustomViewCnl;
		this.manageSingleCustomView = manageSingleCustomView;
		this.cache = cache;
		this.customViewNodeType = customViewNodeType;
		this.customViewContainerUrl = customViewContainerUrl;
		this.customViewContext = customViewContext;
		this.customViewNodeContexts = customViewNodeContext;
		this.customViewPathHints = customViewPathHints;
		this.filterRelativeType = filterRelativeType;
		this.targetType = targetType;
		this.customViewStore = getCustomViewResult(availableCustomViews, selectedCustomViewCnl, this);
		this.mergeDisposers(this.customViewStore);
		this.onSaveCustomView = onSaveCustomView;
		this.OOTBCustomView = OOTBCustomView;
		this.newInstanceFromNode = newInstanceFromNode;
		this.forceDuplicate = forceDuplicate;
	}
}

export const CustomViewDialogStoreContext = createContext<CustomViewDialogStore>(undefined);
export const useCustomViewDialogStore = (): CustomViewDialogStore => useContext(CustomViewDialogStoreContext);